import * as React from 'react'
import { ReactElement, useContext, useMemo, useState } from 'react'
import {
    Button,
    Link,
    makeStyles,
    Menu,
    MenuButton,
    MenuGroupHeader,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    Text,
    tokens,
} from '@fluentui/react-components'
import {
    ArrowClockwiseRegular,
    BookRegular,
    bundleIcon,
    CalendarLtrRegular,
    CallRegular,
    DarkThemeRegular,
    DismissRegular,
    HeadsetFilled,
    HeadsetRegular,
    LocalLanguageRegular,
    MailRegular,
    PlugConnectedRegular,
    StethoscopeRegular,
    ToolboxRegular,
    WeatherMoonRegular,
    WeatherSunnyRegular,
} from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import IconValue from '../../../components/IconValue/IconValueV9'
import DiagnosticsDialog from './DiagnosticsDialog'
import { getApplicationIconPropsByType } from '../../../helpers/iconHelper'
import { DocumentType } from '../../../config/documentConfig'
import { AppAction } from '../../../config/appConfig'
import useLogOut from '../../../hooks/lifecycle/useLogOut'
import useModules from '../../../hooks/module/useModules'
import { ModuleType, ThemeOption } from '../../../context/GlobalContext'
import I18NContext from '../../../context/I18NContext'
import AuthorizationContext from '../../../context/AuthorizationContext'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import useResolvedTheme from '../../../hooks/useResolvedTheme'
import { useApplicationStyles } from '../../../pages/App'

const HeadsetIcon = bundleIcon(HeadsetFilled, HeadsetRegular)

const useStyles = makeStyles({
    surface: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalS,
    },
    contact: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalS,
    },
    actions: {
        display: 'flex',
        columnGap: tokens.spacingHorizontalS,
        justifyContent: 'space-between',
    },
    spacer: {
        flexGrow: 1,
    },
    thinTextButton: {
        fontWeight: tokens.fontWeightRegular,
    },
})

/**
 * v9 replacement for the legacy (Fluent-v8 TeachingBubble/ContextualMenu) Support widget in the footer. A subtle
 * headset Button opens a Popover with contact info plus an action row: the "Hulpmiddelen" Menu, an accentuated
 * "Herstarten" button (HAM-3617) and an X to close. Reuses useLogOut()(AppAction.Reset) for the restart.
 */
export default function Support(): ReactElement {
    const styles = useStyles()
    const appStyles = useApplicationStyles()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()
    const { setLanguage } = useContext(I18NContext)
    const { authenticated } = useContext(AuthorizationContext)
    const modules = useModules()
    const logOut = useLogOut()

    const [open, setOpen] = useState(false)
    const [showDiagnostics, setShowDiagnostics] = useState(false)

    const smallMenu = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])

    const { setThemeOption: setCurrentThemeOption } = useResolvedTheme()

    // Renders the (image-based) application icon props from the shared helper as a plain <img> for v9 menu items.
    const appIcon = (iconProps: ReturnType<typeof getApplicationIconPropsByType>): ReactElement | undefined =>
        iconProps?.imageProps?.src ? <img src={iconProps.imageProps.src as string} width={16} height={16} alt={''} /> : undefined

    const flag = (country: string): ReactElement => <img src={`https://flagcdn.com/16x12/${country}.png`} width={16} height={12} alt={''} />

    const openDiagnostics = (): void => {
        setShowDiagnostics(true)
        setOpen(false)
    }

    return (
        <>
            <Popover open={open} onOpenChange={(_event, data) => setOpen(data.open)} positioning={'above-end'} withArrow trapFocus>
                <PopoverTrigger disableButtonEnhancement>
                    <Button
                        icon={<HeadsetIcon />}
                        appearance={'subtle'}
                        iconPosition={'after'}
                        aria-label={translate('support.label')}
                        className={styles.thinTextButton}
                    >
                        {!smallMenu && translate('support.label')}
                    </Button>
                </PopoverTrigger>

                <PopoverSurface className={styles.surface}>
                    <div className={mergeClasses(styles.actions, appStyles.alignItems)}>
                        <Text weight={'semibold'}>{translate('support.contact.label')}</Text>

                        <Button
                            appearance={'subtle'}
                            icon={<DismissRegular />}
                            aria-label={translate('action.close')}
                            onClick={() => setOpen(false)}
                        />
                    </div>

                    <div className={styles.contact}>
                        <IconValue icon={<CallRegular />}>
                            <Link href={'tel:0262062199'}>026 - 206 21 99</Link>
                        </IconValue>
                        <IconValue icon={<MailRegular />}>
                            <Link href={'mailto:support@hammock.nl'}>support@hammock.nl</Link>
                        </IconValue>
                        <IconValue icon={<CalendarLtrRegular />}>{translate('support.contact.period')}: 8:30 - 17:30</IconValue>
                    </div>

                    <div className={styles.actions}>
                        <Menu positioning={{ autoSize: true }}>
                            <MenuTrigger disableButtonEnhancement>
                                <MenuButton icon={<ToolboxRegular />} appearance={'secondary'}>
                                    {translate('support.toolbox.label')}
                                </MenuButton>
                            </MenuTrigger>
                            <MenuPopover>
                                <MenuList>
                                    <MenuItem icon={<BookRegular />} onClick={() => window.open(process.env.USER_MANUAL_URL, '_blank')}>
                                        {translate('support.toolbox.manual')}
                                    </MenuItem>
                                    <MenuItem
                                        icon={appIcon({ imageProps: { src: './images/anydesk.png' } })}
                                        onClick={() => window.open(process.env.ANY_DESK_URL, '_blank')}
                                    >
                                        {translate('support.toolbox.anydesk')}
                                    </MenuItem>
                                    <MenuItem
                                        icon={appIcon(getApplicationIconPropsByType(DocumentType.TeamViewer, 16))}
                                        onClick={() => window.open(process.env.TEAM_VIEWER_URL, '_blank')}
                                    >
                                        {translate('support.toolbox.teamviewer')}
                                    </MenuItem>

                                    <Menu positioning={{ autoSize: true }}>
                                        <MenuTrigger disableButtonEnhancement>
                                            <MenuItem icon={<PlugConnectedRegular />} disabled={!authenticated}>
                                                {translate('support.toolbox.autorizations.label')}
                                            </MenuItem>
                                        </MenuTrigger>
                                        <MenuPopover>
                                            <MenuList>
                                                <MenuGroupHeader>{translate('support.toolbox.autorizations.accounting')}</MenuGroupHeader>
                                                <MenuItem
                                                    icon={appIcon(getApplicationIconPropsByType(DocumentType.Exact, 16))}
                                                    disabled={!modules.has(ModuleType.Accounting)}
                                                    onClick={() => window.open(process.env.EXACT_AUTH_URL, '_blank')}
                                                >
                                                    Exact Online
                                                </MenuItem>
                                                <MenuItem icon={appIcon(getApplicationIconPropsByType(DocumentType.Twinfield, 16))} disabled>
                                                    Twinfield
                                                </MenuItem>
                                                <MenuItem
                                                    icon={appIcon(getApplicationIconPropsByType(DocumentType.Mollie, 16))}
                                                    disabled={!modules.has(ModuleType.Mollie)}
                                                    onClick={() => window.open(process.env.MOLLIE_AUTH_URL, '_self')}
                                                >
                                                    Mollie
                                                </MenuItem>
                                                <MenuGroupHeader>Microsoft</MenuGroupHeader>
                                                <MenuItem disabled>Microsoft Exchange</MenuItem>
                                                <MenuItem disabled>Microsoft SharePoint</MenuItem>
                                            </MenuList>
                                        </MenuPopover>
                                    </Menu>

                                    <Menu positioning={{ autoSize: true }}>
                                        <MenuTrigger disableButtonEnhancement>
                                            <MenuItem icon={<LocalLanguageRegular />}>{translate('support.toolbox.languages')}</MenuItem>
                                        </MenuTrigger>
                                        <MenuPopover>
                                            <MenuList>
                                                <MenuGroupHeader>{translate('support.toolbox.languages')}</MenuGroupHeader>
                                                <MenuItem icon={flag('nl')} onClick={() => setLanguage('nl')}>
                                                    Nederlands
                                                </MenuItem>
                                                <MenuItem icon={flag('gb')} onClick={() => setLanguage('en')}>
                                                    English
                                                </MenuItem>
                                                {['test', 'dev'].includes(process.env.BOOTSTRAP_ENV) && (
                                                    <MenuItem
                                                        icon={appIcon({ imageProps: { src: './icons/cimode.png' } })}
                                                        onClick={() => setLanguage('cimode')}
                                                    >
                                                        Interactive mode
                                                    </MenuItem>
                                                )}
                                            </MenuList>
                                        </MenuPopover>
                                    </Menu>

                                    <Menu positioning={{ autoSize: true }}>
                                        <MenuTrigger disableButtonEnhancement>
                                            <MenuItem icon={<DarkThemeRegular />}>{translate('support.toolbox.themes')}</MenuItem>
                                        </MenuTrigger>
                                        <MenuPopover>
                                            <MenuList>
                                                <MenuGroupHeader>{translate('support.toolbox.themes')}</MenuGroupHeader>
                                                <MenuItem icon={<WeatherSunnyRegular />} onClick={() => setCurrentThemeOption(ThemeOption.Light)}>
                                                    {translate('support.toolbox.themes.light')}
                                                </MenuItem>
                                                <MenuItem icon={<WeatherMoonRegular />} onClick={() => setCurrentThemeOption(ThemeOption.Dark)}>
                                                    {translate('support.toolbox.themes.dark')}
                                                </MenuItem>
                                                <MenuItem icon={<DarkThemeRegular />} onClick={() => setCurrentThemeOption(ThemeOption.Auto)}>
                                                    {translate('support.toolbox.themes.auto')}
                                                </MenuItem>
                                            </MenuList>
                                        </MenuPopover>
                                    </Menu>

                                    <MenuItem icon={<StethoscopeRegular />} onClick={openDiagnostics}>
                                        {translate('support.toolbox.diagnostics.label')}
                                    </MenuItem>
                                </MenuList>
                            </MenuPopover>
                        </Menu>

                        {/* HAM-3617: restart lives next to "Hulpmiddelen" (one level up from the menu) and is accentuated as primary. */}
                        <Button icon={<ArrowClockwiseRegular />} appearance={'primary'} onClick={() => logOut(AppAction.Reset)}>
                            {translate('support.toolbox.restart')}
                        </Button>
                    </div>
                </PopoverSurface>
            </Popover>

            {authenticated && showDiagnostics && <DiagnosticsDialog open={showDiagnostics} onOpenChange={setShowDiagnostics} />}
        </>
    )
}
